import React, {useEffect, useMemo, useState} from 'react'
import {Node} from 'reactflow'
import {useReactFlow} from 'reactflow'
import {ConditionType, DataNode, MetaDataWithCondition} from '../../FlowTypes'
import {CONDITION, NodeType} from '../../../../../../Utils/Constant/FlowConstants'
import './ConditionalSidebar.scss'
import {toAbsoluteUrl} from '../../../../../../../_metronic/helpers'
import Select, {components, type OptionProps} from 'react-select'
import {useI18n} from '../../../../../../Utils/_helpers/useI18n'

export const CONDITION_TYPES = [
  {value: ConditionType.SENT, label: 'Sent'},
  {value: ConditionType.FAILED, label: 'Failed'},
  {value: ConditionType.DELIVERED, label: 'Delivered'},
  {value: ConditionType.REPLY_CONTAIN, label: 'Response contains'},
  {value: ConditionType.REPLY_START_WITH, label: 'Response starts with'},
  {value: ConditionType.REPLY_END_WITH, label: 'Response ends with'},
  {value: ConditionType.EQUAL, label: 'Response equals'},
  {value: ConditionType.ANY_MESSAGE_EXCEPT_OPTOUT, label: 'Any message except opt-out'},
]

const ConditionalSidebar: React.FC<{node: Node<DataNode<MetaDataWithCondition>, NodeType>}> = ({
  node,
}) => {
  const {setNodes} = useReactFlow()
  const {t} = useI18n()
  const getSafeCondition = (nodeData: typeof node.data): MetaDataWithCondition['condition'] => {
    const condition = nodeData?.metaData?.condition
    if (Array.isArray(condition) && condition.length > 0) {
      return condition
    }
    return [{type: ConditionType.SENT, value: ''}] as MetaDataWithCondition['condition']
  }

  const safeCondition = getSafeCondition(node.data)
  const [condType, setCondType] = useState<ConditionType>(safeCondition[0].type)
  const [values, setValues] = useState<string[]>(
    Array.isArray(safeCondition[0].value)
      ? safeCondition[0].value
      : safeCondition[0].value
      ? [safeCondition[0].value]
      : ['']
  )

  useEffect(() => {
    const safeCondition = getSafeCondition(node.data)
    setCondType(safeCondition[0].type)
    setValues(
      Array.isArray(safeCondition[0].value)
        ? safeCondition[0].value
        : safeCondition[0].value
        ? [safeCondition[0].value]
        : ['']
    )
  }, [node.id])

  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === node.id
          ? {
              ...n,
              data: {
                ...n.data,
                metaData: {
                  ...n.data?.metaData,
                  condition: [
                    {
                      type: condType,
                      value: needsValue(condType) ? values : [],
                    },
                  ],
                },
              },
            }
          : n
      )
    )
  }, [condType, values, node.id, setNodes])

  const typesWithoutValue = [
    ConditionType.SENT,
    ConditionType.FAILED,
    ConditionType.DELIVERED,
    ConditionType.ANY_MESSAGE_EXCEPT_OPTOUT,
  ]
  const needsValue = (type: string) => !typesWithoutValue.includes(type as ConditionType)

  const handleTypeChange = (type: string) => {
    setCondType(type as ConditionType)
  }

  const handleValueChange = (idx: number, value: string) => {
    setValues((vals) => vals.map((v, i) => (i === idx ? value : v)))
  }

  const handleAddValue = () => {
    setValues((vals) => [...vals, ''])
  }

  const handleRemoveValue = (idx: number) => {
    setValues((vals) => (vals.length > 1 ? vals.filter((_, i) => i !== idx) : vals))
  }

  const item = CONDITION[0]
  const conditionOptions = useMemo(() => CONDITION_TYPES, [])

  const getConditionDescription = (type: ConditionType): string => {
    switch (type) {
      case ConditionType.SENT:
        return 'Checks if the message was successfully sent to the carrier.'
      case ConditionType.FAILED:
        return 'Checks if sending failed due to delivery or provider errors.'
      case ConditionType.DELIVERED:
        return 'Checks if the message was delivered to the recipient’s device.'
      case ConditionType.REPLY_CONTAIN:
        return 'Matches when a contact reply contains specific text values.'
      case ConditionType.REPLY_START_WITH:
        return 'Matches when a contact reply starts with any of the provided text values.'
      case ConditionType.REPLY_END_WITH:
        return 'Matches when a contact reply ends with any of the provided text values.'
      case ConditionType.EQUAL:
        return 'Matches when a contact reply equals any of the provided text values.'
      case ConditionType.ANY_MESSAGE_EXCEPT_OPTOUT:
        return 'Matches any inbound message except STOP/opt-out keywords.'
      default:
        return ''
    }
  }

  const OptionWithTooltip = (props: OptionProps<{value: ConditionType; label: string}>) => {
    const desc = getConditionDescription(props.data.value)
    return (
      <components.Option
        {...props}
        innerProps={{
          ...props.innerProps,
          title: desc,
        }}
      />
    )
  }
  return (
    <div className='p-4'>
      <div className='d-flex align-items-center gap-3 mb-4'>
        <div
          className='automation-icon-container'
          style={{
            backgroundColor: `${item.bgColor}`,
          }}
        >
          <img src={toAbsoluteUrl(item.svg || '')} alt={item.icon} className='automation-icon' />
        </div>

        <h3
          className='mb-0 fw-semibold text-truncate'
          title='Conditional Split'
          style={{maxWidth: '220px'}}
        >
          Conditional Split
        </h3>
        <i
          className='bi bi-info-circle ms-1 info'
          title={t('FLOW.SIDEBAR.CONDITIONAL.MORE_INFO')}
          tabIndex={0}
          aria-label={t('FLOW.SIDEBAR.CONDITIONAL.MORE_INFO')}
        ></i>
      </div>

      <div className='condition-stack'>
        <div className='position-relative mb-3' style={{width: '220px', minHeight: '80px'}}>
          <div className='d-flex align-items-center gap-2'>
            <Select
              className='common-select z-1000'
              isSearchable={false}
              options={conditionOptions}
              value={conditionOptions.find((o) => o.value === condType) || conditionOptions[0]}
              onChange={(opt) => handleTypeChange((opt as any)?.value)}
              menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
              closeMenuOnScroll={true}
              menuShouldBlockScroll={false}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: 'var(--bs-gray-200) !important',
                  borderColor: base.borderColor,
                  boxShadow: base.boxShadow,
                  minHeight: 32,
                  width: 220,
                }),
                valueContainer: (base) => ({...base, paddingTop: 2, paddingBottom: 2}),
                indicatorsContainer: (base) => ({...base, paddingTop: 2, paddingBottom: 2}),
              }}
              components={{
                Option: OptionWithTooltip,
              }}
            />
          </div>
          <div className='text-muted small mt-2' style={{maxWidth: 220}}>
            {getConditionDescription(condType)}
          </div>
          {needsValue(condType) && (
            <div className='d-flex flex-column gap-2 mt-2'>
              {values.map((val, idx) => (
                <div key={idx} className='d-flex align-items-center gap-2'>
                  <input
                    type='text'
                    value={val}
                    onChange={(e) => handleValueChange(idx, e.target.value)}
                    className='form-control cs-bg-light cs-border-0 truncate-ellipsis-input'
                    placeholder='Enter value...'
                    style={{width: '170px', height: '38px'}}
                    title={val || 'Enter value...'}
                  />
                  {values.length > 1 && (
                    <button
                      className='btn btn-link text-danger p-0'
                      style={{fontSize: 18}}
                      title='Remove value'
                      onClick={() => handleRemoveValue(idx)}
                    >
                      <i className='bi bi-x-circle'></i>
                    </button>
                  )}
                </div>
              ))}

              <button
                className='btn btn-outline btn-outline-primary d-flex align-items-center gap-2'
                type='button'
                onClick={handleAddValue}
                style={{width: '60%'}}
              >
                <i className='fas fa-plus'></i>
                Add
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ConditionalSidebar
